import { createContext, useContext, useEffect, useState, useCallback, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";

interface AuthContextValue {
  userId: string | null;
  roles: string[];
  hasRole: (role: string) => boolean;
  loading: boolean;
}

const AuthContext = createContext<AuthContextValue>({
  userId: null,
  roles: [],
  hasRole: () => false,
  loading: true,
});

export const useAuth = () => useContext(AuthContext);

/**
 * Provides cached auth state + roles to all children.
 * Queries user_roles once on login, caches in memory.
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  const [userId, setUserId] = useState<string | null>(null);
  const [roles, setRoles] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchRoles = useCallback(async (uid: string) => {
    try {
      const { data, error } = await supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", uid);

      if (error) {
        console.error("[AUTH] Failed to fetch roles:", error);
        setRoles([]);
        return;
      }

      setRoles((data || []).map((r) => r.role));
    } catch (error) {
      console.error("[AUTH] Unexpected role fetch error:", error);
      setRoles([]);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;

    // Safety timeout: never hang on loading for more than 8s
    const timeout = setTimeout(() => {
      if (!cancelled) {
        console.warn("[AUTH] Safety timeout reached — forcing loading=false");
        setLoading(false);
      }
    }, 8000);

    const init = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();

        if (cancelled) return;

        if (session?.user) {
          setUserId(session.user.id);
          setLoading(false);
          void fetchRoles(session.user.id);
          return;
        }

        setUserId(null);
        setRoles([]);
      } catch (error) {
        console.error("[AUTH] Session init failed:", error);
        setUserId(null);
        setRoles([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    init();

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      if (cancelled) return;

      if (session?.user) {
        setUserId(session.user.id);
        setLoading(false);
        void fetchRoles(session.user.id);
      } else {
        setUserId(null);
        setRoles([]);
        setLoading(false);
      }
    });

    return () => {
      cancelled = true;
      clearTimeout(timeout);
      subscription.unsubscribe();
    };
  }, [fetchRoles]);

  const hasRole = useCallback((role: string) => roles.includes(role), [roles]);

  return (
    <AuthContext.Provider value={{ userId, roles, hasRole, loading }}>
      {children}
    </AuthContext.Provider>
  );
}

interface AuthGuardProps {
  children: ReactNode;
  /** Optional required role – checked against cached roles */
  requiredRole?: string;
}

/**
 * Route guard that uses cached roles from AuthProvider.
 * No extra DB query on each route load.
 */
export function AuthGuard({ children, requiredRole }: AuthGuardProps) {
  const navigate = useNavigate();
  const { userId, hasRole, loading } = useAuth();

  useEffect(() => {
    if (loading) return;

    if (!userId) {
      navigate("/auth", { replace: true });
      return;
    }

    if (requiredRole && !hasRole(requiredRole)) {
      navigate("/auth", { replace: true });
    }
  }, [userId, loading, requiredRole, hasRole, navigate]);

  if (loading || !userId) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-pulse text-muted-foreground text-sm">Verifying access…</div>
      </div>
    );
  }

  if (requiredRole && !hasRole(requiredRole)) {
    return null;
  }

  return <>{children}</>;
}
